/* /document/:id — the destination for the "View document" button in a notification email.
 *
 * WHY THIS ROUTE EXISTS AT ALL
 * The email cannot link straight at /api/documents/{id}/download. A link in a mail client opens
 * as a bare browser GET, and this API authenticates by header — the fetch wrapper attaches the
 * identity, a plain navigation cannot (see the resource-token note in core/security.py). The
 * link therefore arrived unauthenticated and the reader was shown a JSON error.
 *
 * The alternative was a signed URL with a lifetime long enough to survive in an inbox, and that
 * was the wrong trade: an email gets forwarded, and a link that carries its own authority hands
 * the document to whoever it is forwarded to. Coming through the application instead means the
 * reader's own access is checked, so a forwarded link is worth nothing to somebody without it.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { request } from '../api/client'
import { errorMessage } from '../ui/chrome'

export default function DocumentRedirect() {
  const { id } = useParams<{ id: string }>()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    /* Replace THIS tab with the document. An earlier version opened a new tab and then sent
     * this one to /chat — a popup blocker eats the tab a script opens outside a click, so the
     * reader pressed "View document" in an email and landed in the chat with no document. */
    request<{ url: string; external: boolean }>(`/api/documents/${id}/link`)
      .then(({ url }) => window.location.replace(url))
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [id])

  return (
    <div className="empty" style={{ padding: 48 }}>
      {error ? (
        <>
          <h2>This document is not available to you</h2>
          <p className="muted">{error}</p>
          <p className="tiny muted">
            Document access follows your Azure AD groups. Contact IT Helpdesk if you believe this
            is wrong.
          </p>
        </>
      ) : (
        <p className="muted">Opening the document…</p>
      )}
    </div>
  )
}
